/* eslint-disable no-unused-vars */
import { groupStage } from "apis/groupStage";
import { CrmTableDropdown } from "components/CrmTableDropdown/CrmTableDropdown";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Chip } from "components/DesignSystem/Chips/Chips";
import DropDown from "components/DesignSystem/Dropdown/Dropdown";

import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { Divider } from "components/design/Divider";
import { Badge } from "components/design/badge";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { PlusIcon } from "components/icons/PlusIcon";
import { PREINCORPORATION } from "constants/onBoarding";
import {
  NOT_CREATED,
  NOT_STARTED,
  NOT_STARTED_KEY,
} from "constants/taskStatus";
import {
  CRM_SUBSCRIPTION_TYPE,
  CRM_SUBSCRIPTION_TYPE_OPTIONS,
} from "dictionaries";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetAssignAgentsQuery } from "store/apis/agents";
import "react-table";
import CrossRed from "static/images/CrossRed.svg";
import GreenTick from "static/images/GreenTick.svg";
import {
  useGetAllSalesCRMGroupsQuery,
  useUpdateGroupDataMutation,
} from "store/apis/salesCrm";
import { useMarkPreIncorporateMutation } from "store/apis/incorporation";
import { SOMETHING_WENT_WRONG } from "../../constants/apiCallError";
import "../../static/styles/components/addTeamMember.css";
import "../../static/styles/components/salesCrm.css";
import CommonTable from "../design/commonTable";
import Loader from "../DesignSystem/Loader/loader";
import { SalesPerson } from "../serviceTeam/SalesPerson";
import TagCreation from "../tagCreation/tagCreation";
import AddCrmEntityTableModal from "./AddCrmEntityTableModal";
import { Header } from "./Header";
import NewGroupCreationModal from "./NewGroupEntityModal";
import ReferrerModal from "./ReferrerModal";
import { useGetAllTaskStatesQuery } from "store/apis/taskTags";
import Modal from "components/DesignSystem/Modal/Modal";
import Table from "components/DesignSystem/Table/V2/Table";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "react-table-8.10.7";
import { TableUI } from "components/design/TableUI";
import { Link } from "components/DesignSystem/Link/Link";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { SolidCheck } from "components/icons/SolidCheck";
import { SolidCross } from "components/icons/SolidCross";
import { Cross } from "components/icons/Cross";
import { CheckOutline } from "components/icons/CheckOutline";
import { useGetCompanyGroupTagsQuery } from "store/apis/crm";
import Tags from "components/serviceTeam/Tags";
import { GROUP_INVITATION_REQUEST_TYPE } from "constants/groupInvitationRequestType";

const OTHER_FILTER_LABEL = {
  STARRED_CHAT: "Starred chat",
  MULTIPLE_US_ENTITIES: "Multiple US entities",
  PRE_INCORPORATE: "Pre-incorporate",
  [GROUP_INVITATION_REQUEST_TYPE.PERSONAL_TAX_INVITE]: "Personal tax invite",
};

const createColumn = createColumnHelper();

const columns = [
  createColumn.accessor("group_name", {
    header: "Group Name",
    size: 20,
    cell: ({ row }) => (
      <>
        {row.original?.name ? (
          <span className="t-flex t-gap-2 t-truncate">
            <ToolTip side="top" text={row.original?.name}>
              <span>
                <Link
                  to={`/admin/crm/${row.original.uuid}`}
                  target="_blank"
                  className="t-truncate"
                >
                  {row.original?.name}
                </Link>
              </span>
            </ToolTip>
            {row.original.subscription_name &&
              row.original.subscription_name !== "NOT_STARTED" && (
                <Badge
                  size="small"
                  color={CRM_SUBSCRIPTION_TYPE[row.original.subscription_name]}
                >
                  {row.original.subscription_name?.[0]}
                </Badge>
              )}
            {row.original?.is_created_from_crm &&
              row.original?.request_type !==
                GROUP_INVITATION_REQUEST_TYPE.PERSONAL_TAX_INVITE && (
                <ToolTip text="Group created from Inkle">
                  <div className="t-flex t-size-6 t-items-center t-justify-center t-rounded t-bg-purple-50 t-align-middle t-text-subtitle t-text-white">
                    i
                  </div>
                </ToolTip>
              )}
            {row.original?.request_type ===
              GROUP_INVITATION_REQUEST_TYPE.PERSONAL_TAX_INVITE && (
              <ToolTip text="Personal tax invite">
                <div className="t-flex t-size-6 t-items-center t-justify-center t-rounded t-bg-purple-50 t-align-middle t-text-subtitle t-text-white">
                  P
                </div>
              </ToolTip>
            )}
            {row.original?.founder?.user_type === PREINCORPORATION && (
              <ToolTip text="Pre-incorporation user">
                <div className="t-rounded t-px-2 t-py-1 t-text-subtext-sm t-text-blue-70 t-bg-blue-10">
                  Pre
                </div>
              </ToolTip>
            )}
          </span>
        ) : (
          <span aria-hidden={true}>{row?.original?.name}</span>
        )}
      </>
    ),
  }),
  createColumn.accessor("tags", {
    header: "Tags",
    size: 13,
    cell: ({ row, table }) => {
      const { tags, updateGroup } = table.options.meta;
      return (
        <Tags
          groupId={row.original.uuid}
          currentTags={row.original?.tags}
          tags={tags}
          updateTags={updateGroup}
        />
      );
    },
  }),
  createColumn.accessor("sales_stage", {
    header: "Sales Person",
    size: 19,
    cell: ({ row }) => {
      const { sales_person, uuid } = row.original;
      return <SalesPerson assignedAgent={sales_person} groupId={uuid} />;
    },
  }),
  createColumn.accessor("referer", {
    header: "Referrer",
    size: 11,
    cell: ({ row, table }) => {
      const { referralModalHandler } = table.options.meta;
      const attribuionExists =
        row.original?.referer?.email ||
        row.original?.coupon_code ||
        row.original?.referer_text;
      return (
        <span>
          {attribuionExists ? (
            row.original?.referer?.email ? (
              <ToolTip side="top" text={row.original?.referer?.event}>
                <span className="t-block t-max-w-[210px] t-truncate t-text-text-60">
                  {row.original?.referer?.email}
                </span>
              </ToolTip>
            ) : (
              <span className="t-block t-max-w-[210px] t-truncate t-text-text-60">
                {row.original?.coupon_code || row.original?.referer_text}
              </span>
            )
          ) : (
            <>
              <DropDown.Root>
                <DropDown.Trigger asChild>
                  <div className="t-flex t-h-8 t-w-8 t-rounded t-p-2 hover:t-bg-i-surface-grey t-text-text-30">
                    <PlusIcon color="currentColor" />
                  </div>
                </DropDown.Trigger>
                <DropDown.Portal>
                  <DropDown.Content className="t-w-[180px] t-py-2">
                    <DropDown.Item
                      onClick={() =>
                        referralModalHandler(row.original?.uuid, "COUPON")
                      }
                    >
                      Add coupon
                    </DropDown.Item>
                    <DropDown.Item
                      onClick={() =>
                        referralModalHandler(row.original?.uuid, "CUSTOMER")
                      }
                    >
                      Add customer
                    </DropDown.Item>
                    <DropDown.Item
                      onClick={() =>
                        referralModalHandler(row.original?.uuid, "TEXT")
                      }
                    >
                      Add text
                    </DropDown.Item>
                  </DropDown.Content>
                </DropDown.Portal>
              </DropDown.Root>
            </>
          )}
        </span>
      );
    },
  }),
  createColumn.accessor("status", {
    header: () => <p className="m-0 t-text-center">Status</p>,
    size: 8.5,
    cell: ({ row, table }) => {
      const { approveHandler, rejectHandler } = table.options.meta;
      const { is_restricted, is_rejected } = row.original;

      if (is_rejected) {
        return (
          <div className="t-flex t-justify-center">
            <SolidCross />
          </div>
        );
      }

      if (is_restricted) {
        return (
          <div className="t-flex t-gap-2 t-justify-center">
            <Button
              size="small"
              customType="icon"
              onClick={() => approveHandler(row.original?.uuid)}
            >
              <CheckOutline />
            </Button>
            <Button
              size="small"
              customType="icon"
              onClick={() => rejectHandler(row.original?.uuid)}
            >
              <Cross />
            </Button>
          </div>
        );
      }

      return (
        <div className="t-flex t-justify-center">
          <SolidCheck size="24" />
        </div>
      );
    },
  }),
  createColumn.accessor("is_pre_incorporate", {
    header: "Pre-incorporate",
    size: 9,
    cell: ({ row, table }) => {
      const { onPreIncorporateChange } = table.options.meta;
      const { is_pre_incorporate, uuid } = row.original;
      return (
        <Switch
          defaultChecked={is_pre_incorporate}
          size="small"
          name="preIncorporateSwitch"
          onCheckedChange={onPreIncorporateChange(uuid)}
        />
      );
    },
  }),
  createColumn.accessor("is_books_subscription_required", {
    header: "Books Pay Wall",
    size: 7.5,
    cell: ({ row, table }) => {
      const { handleBooksPayWallChange } = table.options.meta;
      const { is_books_subscription_required, uuid } = row.original;
      return (
        <Switch
          defaultChecked={is_books_subscription_required}
          size="small"
          name="booksPaywallSwitch"
          onCheckedChange={() =>
            handleBooksPayWallChange(uuid, !is_books_subscription_required)
          }
        />
      );
    },
  }),
  createColumn.accessor("is_perk_redemption_allowed", {
    header: "Perks",
    size: 5,
    cell: ({ row, table }) => {
      const { handlePerksChange } = table.options.meta;
      const { is_perk_redemption_allowed, uuid } = row.original;
      return (
        <Switch
          defaultChecked={is_perk_redemption_allowed}
          size="small"
          name="perkSwitch"
          onCheckedChange={() =>
            handlePerksChange(uuid, !is_perk_redemption_allowed)
          }
        />
      );
    },
  }),
  createColumn.display({
    id: "threedots",
    size: 5,
    cell: ({ row }) => {
      const { uuid, tags } = row.original;
      return (
        <CrmTableDropdown
          actionOn="group"
          uuid={uuid}
          tags={tags}
          showMoveToOpsOption
        />
      );
    },
  }),
];

export default function SalesCrm() {
  usePageTitle("Sales CRM");
  const { alertToast } = useToast();
  const { update } = useUpdateQuery();
  const query = useQuery();
  const { isAdmin } = useRoleBasedView();
  const [showTagModal, setShowTagModal] = useState(false);
  const [groups, setGroups] = useState([]);
  const pageNumber = Number(query.get("page") || 1);
  const [pageCount, setPageCount] = useState(1);
  const searchTerm = query.get("search_term") || null;
  const [searchPageNumber, setSearchPageNumber] = useState(1);
  const [searchPageCount, setSearchPageCount] = useState(1);
  const [load, setLoad] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [stages, setStages] = useState([]);
  const [allStages, setAllStages] = useState([]);
  const [crmStages, setCrmStages] = useState([]);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const currentPageItemCount = groups?.length || 0;
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [userDataPayload, setUserDataPayLoad] = useState();
  const [groupName, setGroupName] = useState("");
  const [userGroupId, setUserGroupId] = useState("");
  const [showAddAttributionModal, setShowAddAttributionModal] = useState(false);
  const [booksPayWall, setBooksPayWall] = useState(null);
  const [perks, setPerks] = useState(null);
  const [showMoveToOpsModal, setShowMoveToOpsModal] = useState(false);
  const viewFilter = "SALES_CRM";
  const selectedTaskState = query.get("task_state_filter_uuids") || null;
  const selectedTag = query.get("tag_filter") || null;
  const selectedSubscription = query.get("subscription_type") || null;
  const salesPersonFilter = query.get("salesperson_filter") || null;
  const otherRadioValue = query.get("other") || null;
  const isPersonalTaxInviteChecked =
    otherRadioValue === GROUP_INVITATION_REQUEST_TYPE.PERSONAL_TAX_INVITE;
  const [taskStates, setTaskStates] = useState([]);
  const [attributionType, setAttributionType] = useState("");
  const [generateReport, setGenerateReport] = useState(false);
  const { data: taskStatesData = [] } = useGetAllTaskStatesQuery({});
  const { data: tags = [] } = useGetCompanyGroupTagsQuery();
  const { data: assignMembers = [] } = useGetAssignAgentsQuery(
    {},
    { skip: !isAdmin }
  );

  useEffect(() => {
    setTaskStates([
      ...taskStatesData,
      { name: NOT_STARTED, uuid: NOT_STARTED_KEY },
      { name: "Not Created", uuid: NOT_CREATED },
    ]);
  }, [showTagModal]);

  const {
    data: group,
    isSuccess,
    isLoading,
    isFetching,
    refetch,
  } = useGetAllSalesCRMGroupsQuery({
    pageNumber: pageNumber,
    searchTerm,
    viewFilter: viewFilter,
    taskState: selectedTaskState,
    generate_report: generateReport,
    selectedTag: selectedTag,
    subscriptionFilter: selectedSubscription,
    salespersonFilter: salesPersonFilter,
    is_starred: otherRadioValue === "STARRED_CHAT",
    has_multiple_us_entities: otherRadioValue === "MULTIPLE_US_ENTITIES",
    is_pre_incorporate: otherRadioValue === "PRE_INCORPORATE",
    request_type: isPersonalTaxInviteChecked
      ? GROUP_INVITATION_REQUEST_TYPE.PERSONAL_TAX_INVITE
      : null,
  });

  const [updateGroup, { data: patchedResp, isLoading: isPatchBtnLoading }] =
    useUpdateGroupDataMutation();
  const [markPreIncorporate] = useMarkPreIncorporateMutation();

  const {
    company_groups = [],
    total_pages = 1,
    total_count = 1,
    current_page = 1,
    per_page = 25,
  } = group || {};

  useEffect(() => {
    if (generateReport && group?.download_url) {
      window.open(group.download_url);
      setGenerateReport(false);
    }
  }, [group?.download_url]);

  useEffect(() => {
    getAllSaleCrm();
  }, [total_pages, total_count, isFetching, group]);

  useEffect(() => {
    const getStages = async () => {
      let viewFilter = "SALES_CRM";
      const { data } = await groupStage({ viewFilter });
      setStages(data);
    };
    const getAllStages = async () => {
      const { data } = await groupStage({});
      setAllStages(data);
    };
    const getCrmStages = async () => {
      let viewFilter = "OPS_CRM";
      const { data } = await groupStage({ viewFilter });
      setCrmStages(data);
    };
    if (isAdmin) {
      getStages();
      getAllStages();
      getCrmStages();
    }
  }, []);

  const updateCompanyStage = useCallback(
    (groupId, companyStage) => {
      setGroups((groups) =>
        crmStages.some((stages) => stages.stage_text === companyStage)
          ? groups.filter((group) => group.uuid !== groupId)
          : groups.map((group) =>
              group.uuid === groupId ? { ...group, stage: companyStage } : group
            )
      );
    },
    [crmStages]
  );

  const getAllSaleCrm = async () => {
    try {
      setGroups(company_groups);
      setPageCount(total_pages);
    } catch (error) {
      alertToast({ message: SOMETHING_WENT_WRONG });
    }
  };

  const approveHandler = (groupId) => {
    setShowModal(true);
    setUserGroupId(groupId);
  };

  const rejectHandler = (groupId) => {
    setShowRejectModal(true);
    setUserGroupId(groupId);
  };

  const referralModalHandler = (groupId, type) => {
    setUserGroupId(groupId);
    setAttributionType(type);
    setShowAddAttributionModal(true);
  };

  const approveUser = async () => {
    try {
      await updateGroup({
        payLoad: { accept_group: true },
        groupId: userGroupId,
      }).unwrap();
    } catch (e) {
      alertToast({ message: e?.data?.error?.message });
    }
    setShowModal(false);
  };

  const rejectUser = async () => {
    try {
      await updateGroup({
        payLoad: { accept_group: false },
        groupId: userGroupId,
      }).unwrap();
    } catch (e) {
      alertToast({ message: e?.data?.error?.message });
    }
    setShowRejectModal(false);
  };

  const moveToOps = async () => {
    try {
      await updateGroup({
        payLoad: { crm_type: "OPS_CRM" },
        groupId: userGroupId,
      }).unwrap();
    } catch (e) {
      alertToast({ message: e?.data?.error?.message });
    }
    setShowMoveToOpsModal(false);
  };

  useEffect(() => {
    if (perks !== null) {
      const updatePerks = async () => {
        try {
          await updateGroup(
            {
              payLoad: { is_perk_redemption_allowed: perks },
              groupId: userGroupId,
            },
            {
              skip: perks,
            }
          ).unwrap();
          setPerks(null);
        } catch (err) {
          alertToast({
            message: err?.data?.error?.message ?? SOMETHING_WENT_WRONG,
          });
        }
      };
      updatePerks();
    }
  }, [perks]);

  useEffect(() => {
    if (booksPayWall !== null) {
      const updateBooksPayWall = async () => {
        try {
          await updateGroup(
            {
              payLoad: { is_books_subscription_required: booksPayWall },
              groupId: userGroupId,
            },
            {
              skip: booksPayWall,
            }
          ).unwrap();
          setBooksPayWall(null);
        } catch (err) {
          alertToast({
            message: err?.data?.error?.message ?? SOMETHING_WENT_WRONG,
          });
        }
      };
      updateBooksPayWall();
    }
  }, [booksPayWall]);

  const onDebtWallChange = (groupId) => async (checked) => {
    try {
      await updateGroup({
        payLoad: { pending_dues_allowed: !checked },
        groupId: groupId,
      }).unwrap();
    } catch (err) {
      alertToast({
        message: err?.data?.error?.message ?? SOMETHING_WENT_WRONG,
      });
    }
  };

  const onPreIncorporateChange = (groupId) => async (checked) => {
    try {
      await markPreIncorporate({
        groupId,
        isPreIncorporate: checked,
      }).unwrap();
    } catch (err) {
      alertToast({
        message: err?.data?.error?.message ?? SOMETHING_WENT_WRONG,
      });
    }
  };

  const pre = true;

  const updateTagFilter = (values) => {
    if (values?.length) {
      update({ query: "tag_filter", value: values.join(",") });
    } else {
      update({ query: "tag_filter", value: null });
    }
  };

  const updateSubscriptionFilter = (values) => {
    if (values?.length) {
      update({ query: "subscription_type", value: values.join(",") });
    } else {
      update({ query: "subscription_type", value: null });
    }
  };

  const updateSalesPersonFilter = (values) => {
    if (values?.length) {
      update({ query: "salesperson_filter", value: values.join(",") });
    } else {
      update({ query: "salesperson_filter", value: null });
    }
  };

  const handleOtherRadioChange = (value) => {
    if (otherRadioValue === value) {
      update({ query: "other", value: null });
    } else {
      update({ query: "other", value: value });
    }
  };

  const handlePersonalTaxInviteChange = () =>
    handleOtherRadioChange(
      isPersonalTaxInviteChecked
        ? ""
        : GROUP_INVITATION_REQUEST_TYPE.PERSONAL_TAX_INVITE
    );

  const isStarredChecked = otherRadioValue === "STARRED_CHAT";
  const isMultipleUsEntitiesChecked =
    otherRadioValue === "MULTIPLE_US_ENTITIES";
  const isPreIncorporateChecked = otherRadioValue === "PRE_INCORPORATE";

  const tagOptions = [
    { value: "NO_TAG", label: "No Tag" },
    ...(tags?.map((tag) => ({ value: tag.title, label: `#${tag.title}` })) ||
      []),
  ];

  const handleBooksPayWallChange = (groupId, value) => {
    setUserGroupId(groupId);
    setBooksPayWall(value);
  };

  const handlePerksChange = (groupId, value) => {
    setUserGroupId(groupId);
    setPerks(value);
  };

  const data = useMemo(() => groups, [groups]);

  const table = useReactTable({
    data,
    columns,
    meta: {
      tags,
      updateGroup,
      referralModalHandler,
      approveHandler,
      rejectHandler,
      onPreIncorporateChange,
      handleBooksPayWallChange,
      handlePerksChange,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.uuid,
    defaultColumn: {
      minSize: 5,
    },
  });

  function getGroups(groups) {
    setGroups(groups);
  }

  const handleClose = () => {
    setShowTagModal(false);
  };

  return (
    <>
      <div className="addTeamMemberContent t-relative t-h-full t-overflow-hidden">
        <div className="form t-flex t-flex-col t-h-full">
          <div className="t-flex">
            <Header
              setShowModal={setShowProfileForm}
              searchedTerm={searchTerm}
              taskStates={taskStates}
              selectedTaskState={selectedTaskState}
              update={update}
              exportCsv={() => setGenerateReport(true)}
              isDownloadingCsv={isFetching}
            />
          </div>

          <div className="t-flex t-mb-3 t-items-center t-gap-3">
            <Filter.Root
              defaultValue="TAG"
              capsule={
                <div className="t-flex t-gap-2">
                  {selectedTag?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => updateTagFilter([])}
                      isActive
                      filterType="TAG"
                    >
                      Tag ({selectedTag.split(",").length})
                    </Chip>
                  )}
                  {selectedSubscription?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => updateSubscriptionFilter([])}
                      isActive
                      filterType="SUBSCRIPTION"
                    >
                      Subscription ({selectedSubscription.split(",").length})
                    </Chip>
                  )}
                  {salesPersonFilter?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => updateSalesPersonFilter([])}
                      isActive
                      filterType="SALESPERSON"
                    >
                      Sales Person ({salesPersonFilter.split(",").length})
                    </Chip>
                  )}
                  {otherRadioValue && (
                    <Chip
                      onRemove={() => update({ query: "other", value: null })}
                      isActive
                      filterType="OTHER"
                    >
                      {OTHER_FILTER_LABEL[otherRadioValue]}
                    </Chip>
                  )}
                </div>
              }
            >
              <Filter.Portal>
                <Filter.List>
                  <Filter.ListItem value="TAG">Tag</Filter.ListItem>
                  {isAdmin && (
                    <Filter.ListItem value="SUBSCRIPTION">
                      Subscription
                    </Filter.ListItem>
                  )}
                  <Filter.ListItem value="SALESPERSON">
                    Sales Person
                  </Filter.ListItem>
                  <Filter.ListItem value="OTHER">Other</Filter.ListItem>
                </Filter.List>
                <Filter.Body value="TAG" block>
                  {tags && (
                    <MultiSelectFilter
                      onChange={updateTagFilter}
                      options={tagOptions}
                      selected={selectedTag?.split(",")}
                    />
                  )}
                </Filter.Body>
                {isAdmin && (
                  <Filter.Body value="SUBSCRIPTION" block>
                    <MultiSelectFilter
                      isSearchable={false}
                      onChange={updateSubscriptionFilter}
                      options={CRM_SUBSCRIPTION_TYPE_OPTIONS}
                      selected={selectedSubscription?.split(",")}
                    />
                  </Filter.Body>
                )}
                <Filter.Body value="SALESPERSON" block>
                  <MultiSelectFilter
                    onChange={updateSalesPersonFilter}
                    options={assignMembers?.map((assignee) => ({
                      value: assignee.profile_id,
                      label: assignee.name,
                    }))}
                    selected={salesPersonFilter?.split(",")}
                  />
                </Filter.Body>
                <Filter.Body value="OTHER" block>
                  <Checkbox
                    label="Starred chat"
                    name="starredChat"
                    onChange={() =>
                      handleOtherRadioChange(
                        isStarredChecked ? "" : "STARRED_CHAT"
                      )
                    }
                    checked={isStarredChecked}
                  />
                  <Checkbox
                    label="Multiple US entities"
                    name="multipleUsEntities"
                    onChange={() =>
                      handleOtherRadioChange(
                        isMultipleUsEntitiesChecked
                          ? ""
                          : "MULTIPLE_US_ENTITIES"
                      )
                    }
                    checked={isMultipleUsEntitiesChecked}
                  />
                  <Checkbox
                    label="Pre-incorporate"
                    name="preIncorporate"
                    onChange={() =>
                      handleOtherRadioChange(
                        isPreIncorporateChecked ? "" : "PRE_INCORPORATE"
                      )
                    }
                    checked={isPreIncorporateChecked}
                  />
                  <Checkbox
                    label="Personal tax invite"
                    name="personalTaxInvite"
                    onChange={handlePersonalTaxInviteChange}
                    checked={isPersonalTaxInviteChecked}
                  />
                </Filter.Body>
              </Filter.Portal>
            </Filter.Root>
          </div>

          <div className="t-flex t-mb-3 t-justify-end">
            <PaginationHandler
              page_num={pageNumber}
              per_page={per_page}
              total_count={total_count}
              total_pages={total_pages}
            />
          </div>

          {isLoading || !isSuccess ? (
            <Loader />
          ) : (
            <div className="addTeamMemberContentBody t-flex-1 t-min-h-0">
              <Modal.Root
                open={showModal}
                onOpenChange={() => setShowModal(false)}
              >
                <Modal.Content>
                  <Modal.Header>
                    <Modal.Title>Approve the user?</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body>
                    <div className="t-text-body-lg t-text-text-black">
                      Are you sure that you want to approve the user?
                    </div>
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button onClick={() => setShowModal(false)}>
                        Cancel
                      </Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      isLoading={isPatchBtnLoading}
                      onClick={approveUser}
                    >
                      Approve
                    </Button>
                  </Modal.FooterButtonGroup>
                </Modal.Content>
              </Modal.Root>

              <Modal.Root
                open={showRejectModal}
                onOpenChange={() => setShowRejectModal(false)}
              >
                <Modal.Content>
                  <Modal.Header>
                    <Modal.Title>Reject the user?</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body>
                    <div className="t-text-body-lg t-text-text-black">
                      Are you sure that you want to reject the user?
                    </div>
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button onClick={() => setShowRejectModal(false)}>
                        Cancel
                      </Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      isLoading={isPatchBtnLoading}
                      onClick={rejectUser}
                    >
                      Reject
                    </Button>
                  </Modal.FooterButtonGroup>
                </Modal.Content>
              </Modal.Root>

              <Modal.Root
                open={showMoveToOpsModal}
                onOpenChange={() => setShowMoveToOpsModal(false)}
              >
                <Modal.Content>
                  <Modal.Header>
                    <Modal.Title>Move the group to Ops?</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body>
                    <div className="t-text-body-lg t-text-text-black">
                      Are you sure that you want to move the group to Ops CRM?
                    </div>
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Button onClick={() => setShowMoveToOpsModal(false)}>
                      No
                    </Button>
                    <Button
                      customType="primary"
                      isLoading={isPatchBtnLoading}
                      onClick={moveToOps}
                    >
                      Yes
                    </Button>
                  </Modal.FooterButtonGroup>
                </Modal.Content>
              </Modal.Root>

              <ReferrerModal
                groups={groups}
                pageNumber={pageNumber}
                searchTerm={searchTerm}
                viewFilter={viewFilter}
                showAddAttributionModal={showAddAttributionModal}
                setShowAddAttributionModal={setShowAddAttributionModal}
                updateGroup={updateGroup}
                groupId={userGroupId}
                attributionType={attributionType}
              />

              {showProfileForm && (
                <NewGroupCreationModal
                  showModal={showProfileForm}
                  setShowModal={setShowProfileForm}
                  setShowEntityModal={setShowEntityModal}
                  setUserDataPayLoad={setUserDataPayLoad}
                  userDataPayload={userDataPayload}
                  setGroupName={setGroupName}
                />
              )}
              {showEntityModal && (
                <AddCrmEntityTableModal
                  setShowGroupModal={setShowProfileForm}
                  showModal={showEntityModal}
                  setModalShow={setShowEntityModal}
                  userDataPayload={userDataPayload}
                  groupName={groupName}
                />
              )}
              <div className="addTeamMemberSelectTable t-flex-1 t-min-h-0 t-overflow-auto">
                <TableUI
                  layout="fixed"
                  table={table}
                  height="100%"
                  contentClassName="t-min-w-[900px]"
                />
              </div>
              <div className="t-flex t-p-4 t-justify-end t-w-full t-shrink-0">
                <PaginationHandler
                  page_num={pageNumber}
                  per_page={per_page}
                  total_count={total_count}
                  total_pages={total_pages}
                />
              </div>
            </div>
          )}
        </div>
        <TagCreation show={showTagModal} handleClose={handleClose} />
      </div>
    </>
  );
}
